import { useEffect, useMemo, useState } from 'react'
import { RateCard } from '../components/RateCard'
import { TRADES, tradeBucket, tradeName } from '../data/trades'
import { useLocale, useT } from '../i18n'
import { usePageMeta } from '../lib/meta'
import { type LaneRow, getLanes } from '../lib/rates'

export function LanesHubPage() {
  const t = useT()
  const locale = useLocale()
  usePageMeta(t('meta.lanes.title'), t('meta.lanes.desc'), '/rates')
  const [lanes, setLanes] = useState<LaneRow[]>([])
  const [trade, setTrade] = useState<number | null>(null)

  useEffect(() => {
    getLanes()
      .then(setLanes)
      .catch(() => {})
  }, [])

  // Classify by destination (frm_trade_bucket), NOT the raw line_category_id — that column is an
  // unrelated FreighTower grouping and would show 0 for Asia–Europe / Asia–North America.
  const counts = useMemo(() => {
    const m = new Map<number, number>()
    for (const l of lanes) {
      const b = tradeBucket(l.pod_code)
      m.set(b, (m.get(b) ?? 0) + 1)
    }
    return m
  }, [lanes])
  const shown = trade ? lanes.filter((l) => tradeBucket(l.pod_code) === trade) : lanes

  return (
    <div className="mx-auto max-w-6xl px-5 pt-12">
      <h1 className="font-display text-4xl font-light">{t('lanes.h1')}</h1>
      <p className="mt-3 max-w-2xl text-muted">{t('lanes.intro')}</p>

      {/* trade filter chips */}
      <div className="mt-6 flex flex-wrap gap-2">
        <Chip
          active={trade === null}
          onClick={() => setTrade(null)}
          label={`${t('lanes.all')} (${lanes.length})`}
        />
        {TRADES.map((tr) => (
          <Chip
            key={tr.id}
            active={trade === tr.id}
            onClick={() => setTrade(tr.id)}
            label={`${tradeName(tr, locale).short} (${counts.get(tr.id) ?? 0})`}
          />
        ))}
      </div>

      {lanes.length === 0 ? (
        <div className="mt-6 rounded-xl border border-border-strong bg-surface-0 p-8 text-center text-muted">
          {t('lanes.loading')}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((l) => (
            <RateCard key={`${l.pol_code}-${l.pod_code}`} lane={l} />
          ))}
        </div>
      )}
    </div>
  )
}

function Chip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'border-signal bg-signal/10 text-signal'
          : 'border-border-strong text-muted hover:text-ink hover:border-signal'
      }`}
    >
      {label}
    </button>
  )
}
